/**
 * The only sanctioned ways to reach a URL STRING or to navigate without React
 * context. Every function takes the router explicitly — nothing here imports
 * the browser singleton, which would create a
 * router → eager pages/auth → navigation → router load cycle.
 *
 *   navigating      ──►  useNavigate() / <Link {...appLinks.x()}>   NOT this module
 *   rendering href  ──►  buildAppHref(router, appLinks.x())
 *                        legitimate in React when a component needs the STRING:
 *                        MemberNavigation feeds hrefs to SidebarLink, and
 *                        window.open / clipboard / stored document URLs.
 *   SDK callback    ──►  navigateHref(router, hrefFromSdk)
 *
 * An already-built href carries no route types, so those helpers take any
 * router — the Storybook/test router included. `buildAppHref` resolves a typed
 * destination and therefore stays on the app router.
 */
import type { AnyRouter } from '@tanstack/router-core';
import type { AppLinkOptions, AppNavigationBehavior } from 'src/routing/appLinks';
import type { AppRouter } from 'src/routing/router';

export function navigateHref(router: AnyRouter, href: string, behavior: AppNavigationBehavior = {}): Promise<void> {
  return router.navigate({ ...behavior, href });
}

export function buildAppHref(router: AppRouter, options: AppLinkOptions): string {
  return router.buildLocation(options).publicHref;
}

function createAppHref(router: AnyRouter, href: string): string {
  return router.history.createHref(href);
}

/** React Aria's `RouterProvider` contract: spread into it so `<AriaLink href>` stays an SPA navigation. */
type AriaRouterProps = Readonly<{ navigate: (href: string) => void; useHref: (href: string) => string }>;

export function createAriaRouterProps(router: AnyRouter): AriaRouterProps {
  return {
    navigate: (href) => {
      void navigateHref(router, href);
    },
    useHref: (href) => createAppHref(router, href),
  };
}

/** Clerk hands back origin-stripped app hrefs and distinguishes push from replace. */
export type ClerkRouterNavigation = Readonly<{ push: (href: string) => void; replace: (href: string) => void }>;

export function createClerkRouterNavigation(router: AnyRouter): ClerkRouterNavigation {
  return {
    push: (href) => {
      void navigateHref(router, href);
    },
    replace: (href) => {
      void navigateHref(router, href, { replace: true });
    },
  };
}
